import { useState } from 'react';
import { Link as LinkIcon, Package, Plus, Trash2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useCreateAllocation, useDeleteAllocation } from '../hooks/useDebts.js';
import { formatCurrency } from '../lib/format.js';
import Button from './Button.jsx';

const inputClass =
  'w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400 disabled:bg-slate-50 disabled:text-slate-400';

function AddAllocationForm({ debtId, unallocatedCents, onDone }) {
  const createAllocation = useCreateAllocation();
  const [form, setForm]   = useState({ lot_number: '', amount: '' });
  const [error, setError] = useState(null);

  const remaining = unallocatedCents ?? 0;

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);

    const lotNumber = parseInt(form.lot_number, 10);
    const cents     = Math.round(parseFloat(form.amount) * 100);

    if (!lotNumber || lotNumber <= 0) return setError('Numéro de lot requis');
    if (!cents || cents <= 0)         return setError('Montant requis (> 0)');
    if (cents > remaining) {
      return setError(`Il ne reste que ${formatCurrency(remaining)} non affectés sur cette dette`);
    }

    try {
      await createAllocation.mutateAsync({ debtId, lotNumber, allocatedCents: cents });
      setForm({ lot_number: '', amount: '' });
      onDone?.();
    } catch (err) {
      setError(err?.message ?? 'Échec de l\'affectation');
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3 border-t border-slate-100 pt-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label className="block">
          <span className="text-xs text-slate-500 mb-1 block">Numéro de lot</span>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-400 pointer-events-none">LT</span>
            <input
              type="number"
              className={`${inputClass} pl-8`}
              value={form.lot_number}
              onChange={(e) => setForm((f) => ({ ...f, lot_number: e.target.value }))}
              placeholder="1156"
              min="1"
              step="1"
              disabled={createAllocation.isPending}
            />
          </div>
        </label>

        <label className="block">
          <span className="text-xs text-slate-500 mb-1 block">
            Montant affecté (€)
            <span className="text-slate-400"> · {formatCurrency(remaining)} dispo</span>
          </span>
          <input
            type="number"
            className={inputClass}
            value={form.amount}
            onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
            placeholder="7500"
            min="0"
            step="0.01"
            disabled={createAllocation.isPending}
          />
        </label>
      </div>

      {error && <p className="text-xs text-red-600">{error}</p>}

      <Button type="submit" loading={createAllocation.isPending} disabled={remaining <= 0}>
        {!createAllocation.isPending && <Plus size={14} aria-hidden />}
        Affecter au lot
      </Button>
    </form>
  );
}

/**
 * Affectation d'une dette à un ou plusieurs lots.
 *
 * Le reliquat non affecté est la vraie information : une dette de 10 000 € dont
 * 7 500 € financent un lot laisse 2 500 € encore disponibles.
 */
export default function DebtLotAllocations({ debt }) {
  const deleteAllocation = useDeleteAllocation();
  const [showForm, setShowForm] = useState(false);

  const allocations = debt.allocations ?? [];
  const allocated   = debt.allocated_cents ?? 0;
  const unallocated = debt.unallocated_cents ?? 0;
  const capital     = debt.capital_cents ?? 0;
  const pct         = capital > 0 ? Math.round((allocated / capital) * 100) : 0;

  async function handleDelete(allocation) {
    if (!confirm(`Retirer l'affectation de ${formatCurrency(allocation.allocated_cents)} au lot LT${allocation.lot_number} ?`)) return;
    await deleteAllocation.mutateAsync({ debtId: debt.id, allocationId: allocation.id });
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm px-4 py-4 space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Package size={16} className="text-slate-500 shrink-0" aria-hidden />
          <h2 className="text-sm font-semibold text-slate-800">Lots financés</h2>
        </div>
        {!showForm && unallocated > 0 && (
          <button
            type="button"
            onClick={() => setShowForm(true)}
            className="text-xs text-slate-600 hover:text-slate-900 underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 rounded"
          >
            Affecter un lot
          </button>
        )}
      </div>

      {/* Affecté vs non affecté */}
      <div>
        <div className="flex justify-between text-[11px] text-slate-500 mb-1">
          <span className="tabular-nums">{formatCurrency(allocated)} affectés</span>
          <span className="tabular-nums">
            {unallocated > 0 ? `${formatCurrency(unallocated)} non affectés` : 'Intégralement affectée'}
          </span>
        </div>
        <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
          <div
            className="h-full rounded-full bg-slate-700 transition-all"
            style={{ width: `${Math.min(100, pct)}%` }}
          />
        </div>
      </div>

      {allocations.length === 0 ? (
        <p className="text-sm text-slate-500">
          Aucun lot affecté à cette dette.
        </p>
      ) : (
        <ul className="divide-y divide-slate-100">
          {allocations.map((a) => (
            <li key={a.id} className="flex items-center justify-between gap-3 py-2">
              <Link
                to={`/lots/${a.lot_number}`}
                className="flex items-center gap-2 text-sm font-medium text-slate-800 hover:text-slate-950 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 rounded"
              >
                <LinkIcon size={13} className="text-slate-400 shrink-0" aria-hidden />
                LT{a.lot_number}
              </Link>
              <div className="flex items-center gap-3">
                <span className="text-sm tabular-nums text-slate-700">
                  {formatCurrency(a.allocated_cents)}
                </span>
                <button
                  type="button"
                  onClick={() => handleDelete(a)}
                  disabled={deleteAllocation.isPending}
                  aria-label={`Retirer l'affectation au lot LT${a.lot_number}`}
                  className="text-slate-400 hover:text-red-600 disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-400 rounded p-1"
                >
                  <Trash2 size={14} aria-hidden />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {showForm && (
        <AddAllocationForm
          debtId={debt.id}
          unallocatedCents={unallocated}
          onDone={() => setShowForm(false)}
        />
      )}
    </div>
  );
}
